import { Link } from "react-router-dom";
import { Type, Image, Calendar, ArrowRight } from "lucide-react";
import { supabase } from "../lib/supabaseClient";
import { useEffect, useMemo, useState } from "react";
import ErrorBoundary from "../components/ErrorBoundary";
import StarterCalendarPreview from "../components/StarterCalendarPreview";
import { safeCapture } from "../utils/analytics";


export default function Home() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Compact Hero - app-style (NO logo since it's in header) */}
      <section className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Your Tools
        </h1>
        <p className="text-sm text-gray-600 mb-6">
          AI-powered content creation for small business owners
        </p>

        {/* Optional: Small branded image as accent */}
        <div className="rounded-lg overflow-hidden shadow-sm ring-1 ring-black/5 mb-6">
          <img
            src="/branded-laptop.jpg"
            alt="Bluedobie creative workspace"
            className="w-full h-32 object-cover"
          />
        </div>
      </section>

      {/* Tools Grid - proper elevated cards */}
      <section className="mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Link
  to="/prewriting"
  onClick={() => safeCapture("home_tool_clicked", { tool: "prewriting" })}
  className="group bg-white rounded-xl shadow-sm hover:shadow-lg border border-gray-100 p-6 transition-all duration-200 hover:border-sabre-blue hover:-translate-y-1"
>
  <div className="flex items-start justify-between mb-4">
    <div className="flex items-center gap-3">
      <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
        <img
          src="/DobieCore-Prewriting.png"
          alt="DobieCore Prewriting Tools"
          className="w-10 h-10 object-contain rounded-lg border border-gray-200 shadow-sm"
        />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Prewriting Tools
        </h3>
        <p className="text-xs text-green-600 font-semibold uppercase tracking-wide">
          Available
        </p>
      </div>
    </div>

    <ArrowRight
      className="text-gray-300 group-hover:text-sabre-blue group-hover:translate-x-1 transition-all"
      size={20}
    />
  </div>

  <p className="text-sm text-gray-600 leading-relaxed">
    Think through topics, headlines, and SEO—before writing a word.
  </p>
</Link>
          {/* Captions - Available */}
          <Link
            to="/captions"
            onClick={() => safeCapture("home_tool_clicked", { tool: "captions" })}
            className="group bg-white rounded-xl shadow-sm hover:shadow-lg border border-gray-100 p-6 transition-all duration-200 hover:border-sabre-blue hover:-translate-y-1"
          >
            
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                  <img
            src="/dobiecore-logo.png"
            alt="DobieCore Social Captions"
            className="w-full h-12 object-cover shadow-md rounded-lg border border-gray-200"
          />
                 
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    Social Captions
                  </h3>
                  <p className="text-xs text-green-600 font-semibold uppercase tracking-wide">Available</p>
                </div>
              </div>
              <ArrowRight
                className="text-gray-300 group-hover:text-sabre-blue group-hover:translate-x-1 transition-all"
                size={20}
              />
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              Generate brand voice captions that sound like you, not a robot
            </p>
          </Link>

          {/* Image Prompts - Available */}
<Link
  to="/images"
  onClick={() => safeCapture("home_tool_clicked", { tool: "images" })}
  className="group bg-white rounded-xl shadow-sm hover:shadow-lg border border-gray-100 p-6 transition-all duration-200 hover:border-sabre-blue hover:-translate-y-1"
>
  <div className="flex items-start justify-between mb-4">
    <div className="flex items-center gap-3">
      <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
        <img
          src="/DobieCore_images_logo.png"
          alt="DobieCore Images"
          className="w-10 h-10 object-contain rounded-lg border border-gray-200 shadow-sm"
        />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Image Prompts
        </h3>
        <p className="text-xs text-green-600 font-semibold uppercase tracking-wide">
          Available
        </p>
      </div>
    </div>
    <ArrowRight
      className="text-gray-300 group-hover:text-sabre-blue group-hover:translate-x-1 transition-all"
      size={20}
    />
  </div>
  <p className="text-sm text-gray-600 leading-relaxed">
    Turn ideas into detailed prompts for social, web, branding, and Canva-ready images.
  </p>
</Link>
{/* Blog Writer - Available */}
<Link
  to="/blog"
  onClick={() => safeCapture("home_tool_clicked", { tool: "blog" })}
  className="group bg-white rounded-xl shadow-sm hover:shadow-lg border border-gray-100 p-6 transition-all duration-200 hover:border-sabre-blue hover:-translate-y-1"
>
  <div className="flex items-start justify-between mb-4">
    <div className="flex items-center gap-3">
      <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
        <img
          src="/DobieCore-Blog-Logo.png"
          alt="DobieCore Blogs"
          className="w-10 h-10 object-contain rounded-lg border border-gray-200 shadow-sm"
        />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Blog Writer
        </h3>
        <p className="text-xs text-green-600 font-semibold uppercase tracking-wide">
          Available
        </p>
      </div>
    </div>

    <ArrowRight
      className="text-gray-300 group-hover:text-sabre-blue group-hover:translate-x-1 transition-all"
      size={20}
    />
  </div>

  <p className="text-sm text-gray-600 leading-relaxed">
    Generate clear, educational blog posts with title, meta description, and 3 image prompts.
  </p>
</Link>

          {/* Content Calendar - Coming Soon */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center">
                  <Calendar className="text-gray-400" size={22} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    Content Calendar
                  </h3>
                  <p className="text-xs text-green-600 font-semibold uppercase tracking-wide">Available</p>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed mb-4">
              Download an import-ready 4-week starter content calendar you can upload into most social schedulers.
            </p>
            <a
              href="/DobieCore_4-Week_Starter_Content_Calendar.csv"
              download="DobieCore_4-Week_Starter_Content_Calendar.csv"
              onClick={() => safeCapture("home_calendar_download_clicked")}
              className="inline-flex items-center text-sm font-semibold text-blue-600 hover:text-blue-700"
            >
              Download Starter Content Calendar (CSV)
  </a>
            <ErrorBoundary>
  <StarterCalendarPreview />


</ErrorBoundary>
        </div>
        </div>
      </section>

      {/* Quick Start Guide */}
      <section className="bg-blue-50 border border-blue-100 rounded-lg p-6 mb-8">
        <h3 className="text-base font-semibold text-gray-900 mb-3">
          👋 Getting Started
        </h3>
        <div className="space-y-2 text-sm text-gray-700">
          <p>
            <strong>1. Start with Captions:</strong> Generate 3 platform-optimized caption variations in seconds
          </p>
          <p>
            <strong>2. Choose your platform:</strong> Select from Instagram, Facebook, LinkedIn, and more
          </p>
          <p>
            <strong>3. Copy & post:</strong> One click to copy, then paste directly into your social platform
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="mt-12 pt-6 border-t border-gray-200 text-center">
        <p className="text-sm text-gray-500">
          Built with 💙 by Bluedobie Developing
        </p>
        <p className="text-sm text-gray-500 mt-2">
          <Link to="/privacy" className="underline hover:text-gray-700">
            Privacy Notice
          </Link>
        </p>
      </footer>
    </div>
  );
}